"use client";

import { useScrollAnimation } from "../hooks/useScrollAnimation";
import socialIreland from "../src/assets/socialIreelandLogo.webp";
import enterpriseIreland from "../src/assets/enterpriseLogo.webp";
import team from "../src/assets/team.webp";

export default function OurStory() {
  const heroAnimation = useScrollAnimation({ delay: 0 });
  const storyAnimation = useScrollAnimation({ delay: 200 });
  const quoteAnimation = useScrollAnimation({ delay: 400 });
  const buildingAnimation = useScrollAnimation({ delay: 600 });
  const supportAnimation = useScrollAnimation({ delay: 800 });
  const impactAnimation = useScrollAnimation({ delay: 1000 });
  // Scroll-triggered animation for Hero Section heading
  const headingAnimation = useScrollAnimation({ delay: 100 });

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Hero Section */}
      <div className="max-w-4xl mx-auto px-6 pt-32">
        <div
          ref={headingAnimation.ref}
          className={`py-14 text-center transition-opacity duration-700 ease-out ${
            headingAnimation.isVisible ? "animate-slide-up" : "opacity-0"
          }`}
        >
          <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold text-[#2b2b2b] mb-6">
            Our{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-purple-800">
              Story:
            </span>{" "}
            From Research to Real-World Impact
          </h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-3xl mx-auto px-6 md:px-0 pb-24 space-y-20">
        {/* From Shared Research to Shared Vision */}
        <section
          ref={storyAnimation.ref}
          className={`px-4 md:px-0 transition-all duration-700 ${storyAnimation.isVisible ? "animate-slide-up" : "opacity-0"}`}
        >
          <h2 className="text-2xl sm:text-3xl font-semibold text-[#2b2b2b] mb-8">
            From Shared Research to Shared Vision
          </h2>

          <div className="space-y-6 text-base sm:text-xl text-[#2b2b2b] leading-relaxed">
            <p>
              AnamVR was born from a shared frustration — and a shared idea.
            </p>

            <p>
              Co-founders Rob Sheridan and Pierce O'Brien had unknowingly spent
              years exploring the same topic for their respective theses: how
              virtual reality could be used to support mental health. One a
              psychotherapist, the other a game developer, they approached the
              problem from different angles — but with the same core belief:
              that immersive technology had the power to improve wellbeing in a
              more human way.
            </p>

            <p>
              After working together on related projects for several years, Rob
              and Pierce discovered this shared focus and mutual passion.
              Frustrated by wellness tools that felt clinical, outdated, or
              simply uninspiring, they decided to apply their complementary
              skills — in therapy and game design — to build something better.
            </p>
          </div>
        </section>

        {/* Quote Section */}
        <section
          ref={quoteAnimation.ref}
          className={`px-4 sm:px-0 transition-all duration-700 ${quoteAnimation.isVisible ? "animate-slide-up" : "opacity-0"}`}
        >
          <div className="border-l-2 border-gray-200 pl-6 mb-12">
            <blockquote className="text-base sm:text-xl text-gray-800 italic leading-relaxed">
              "We didn't just want to build another app. We wanted to reimagine
              what mental health support could feel like — accessible, engaging,
              and built for real life."
            </blockquote>
          </div>

          <img
            src={team || "/placeholder.svg"}
            alt="Founders of AnamVR"
            className="w-full rounded-lg shadow-sm"
          />
        </section>

        {/* Building Something Different */}
        <section
          ref={buildingAnimation.ref}
          className={`transition-all duration-700 ${buildingAnimation.isVisible ? "animate-slide-up" : "opacity-0"}`}
        >
          <h2 className="text-2xl sm:text-3x font-semibold text-[#2b2b2b] mb-8">
            Building Something Different
          </h2>

          <div className="text-base sm:text-xl space-y-6 text-[#2b2b2b] leading-relaxed">
            <p>
              That vision became AnamVR — a wellness platform that combines
              evidence-based therapy with immersive, smartphone-delivered VR
              experiences and gamified self-care tools.
            </p>

            <p>
              From the beginning, AnamVR has focused on reducing the barriers to
              support: cost, access, stigma, and usability. Every scene, tool,
              and experience is designed to feel intuitive, approachable, and
              emotionally engaging.
            </p>
          </div>
        </section>

        {/* Support Section */}
        <section
          ref={supportAnimation.ref}
          className={`px-4 md:px-0 py-12 transition-all duration-700 ${supportAnimation.isVisible ? "animate-slide-up" : "opacity-0"}`}
        >
          <h1 className="text-[#2b2b2b] text-center text-2xl sm:text-3x font-bold leading-relaxed mb-8">
            We've been supported by
          </h1>

          <div className="flex flex-row justify-center items-center gap-6 sm:gap-12">
            <img
              src={socialIreland || "/placeholder.svg"}
              alt="Social Entrepreneurs Ireland"
              className="h-8 sm:h-10 md:h-12 lg:h-16 w-auto object-contain opacity-90 hover:opacity-100 transition-opacity"
            />
            <img
              src={enterpriseIreland || "/placeholder.svg"}
              alt="Enterprise Ireland"
              className="h-8 sm:h-10 md:h-12 lg:h-16 w-auto object-contain opacity-90 hover:opacity-100 transition-opacity"
            />
          </div>
        </section>

        {/* Measurable Impact */}
        <section
          ref={impactAnimation.ref}
          className={`px-4 md:px-0 transition-all duration-700 ${impactAnimation.isVisible ? "animate-slide-up" : "opacity-0"}`}
        >
          <h2 className="text-2xl sm:text-3x font-semibold text-[#2b2b2b]">
            Measurable Impact
          </h2>

          <div className="flex flex-col items-center space-y-8  pb-8">
            {/* Simple Growth Illustration */}
            <div className="relative w-full max-w-xs sm:max-w-sm md:max-w-2xl lg:max-w-4xl h-40 sm:h-44 md:h-56 lg:h-64 flex items-end justify-center gap-4 sm:gap-6">
              {/* Research Phase */}
              <div className="flex flex-col items-center">
                <div className="w-12 h-16 sm:h-20 md:h-24 lg:h-28 bg-gray-200 rounded-t-lg"></div>
                <span className="text-xs text-gray-500 mt-2">Research</span>
              </div>

              {/* Development Phase */}
              <div className="flex flex-col items-center">
                <div className="w-12 h-20 sm:h-24 md:h-28 lg:h-32 bg-gray-300 rounded-t-lg"></div>
                <span className="text-xs text-gray-500 mt-2">Development</span>
              </div>

              {/* Launch Phase */}
              <div className="flex flex-col items-center">
                <div className="w-12 h-24 sm:h-28 md:h-32 lg:h-36 bg-purple-200 rounded-t-lg"></div>
                <span className="text-xs text-gray-500 mt-2">Launch</span>
              </div>

              {/* Impact Phase */}
              <div className="flex flex-col items-center">
                <div className="w-12 h-28 sm:h-32 md:h-36 lg:h-40 bg-purple-400 rounded-t-lg relative"></div>
                <span className="text-xs text-gray-500 mt-2">Impact</span>
              </div>

              {/* Future Phase */}
              <div className="flex flex-col items-center">
                <div className="w-12 h-32 sm:h-36 md:h-40 lg:h-44 bg-gradient-to-t from-purple-400 to-purple-600 rounded-t-lg relative"></div>
                <span className="text-xs text-gray-500 mt-2">Future</span>
              </div>
            </div>
          </div>

          <div className="text-base sm:text-xl space-y-6 text-[#2b2b2b] leading-relaxed">
            <p>
              Since launch, we've supported over{" "}
              <strong className="text-[#2b2b2b]">1,000 individuals</strong>{" "}
              across Ireland and beyond — from students and frontline workers to
              remote employees and community care providers. Users have reported
              measurable improvements in anxiety, mood, stress management, and
              emotional awareness.
            </p>

            <p className="text-lg font-medium text-[#2b2b2b]">
              And we're just getting started.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}
